// src/components/billing/StudentBillingDialog.js
//
// FICHE DE SUIVI D'UN ÉLÈVE (ou d'une fratrie) : toutes ses inscriptions
// regroupées, avec pour chacune :
//   • l'échéancier (dates, montants, payé / en retard / à venir) ;
//   • l'encaissement manuel d'une échéance (et son annulation) ;
//   • les réglages de l'échéancier pour les abonnements hors Mollie ;
//   • l'historique des paiements Mollie ;
// et, pour l'ensemble, les présences réellement saisies (feuilles de présence
// + cartes 10), chacune rattachée à la formule qui la couvre. Une présence
// d'un titulaire de carte 10 qui n'a pas été décomptée peut l'être en un clic.

import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { db } from '../../firebase';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button,
  Chip, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  IconButton, Tooltip, LinearProgress, CircularProgress, Alert, TextField,
  Collapse, Grid, Divider,
} from '@mui/material';
import {
  Delete as DeleteIcon, OpenInNew as OpenIcon, Tune as TuneIcon,
  Payments as PayIcon, CreditCard as CardIcon,
} from '@mui/icons-material';
import {
  computeBilling, registrationKind, registrationStartDate, registrationCourseIds,
  registrationCourseLabel, registrationPersons, personIdentityKeys, planLabel,
  cardCredits, consumptionMatches, billingStatusMeta, INSTALLMENT_STATUS,
  PAYMENT_DOC_STATUS, METHOD_LABELS, MANUAL_METHOD_LABELS, FREQUENCY_LABELS,
  PAYMENT_MODE_LABELS, dayKey, formatDay, money, readDate, round2, seasonOf, normalizeText,
} from '../../utils/billing';
import {
  removeManualPayment, saveBillingOverrides, consumeCardCredit,
} from '../../utils/billingActions';
import RecordPaymentDialog from './RecordPaymentDialog';

const PURPLE = '#7C3AED';

const StatusChip = ({ meta, suffix }) => (
  <Chip
    size="small"
    label={suffix ? `${meta.label} ${suffix}` : meta.label}
    sx={{ backgroundColor: meta.bg, color: meta.color, fontWeight: 600 }}
  />
);

const KIND_ORDER = { subscription: 0, card: 1, single: 2, trial: 3 };

// ── Chargement des présences d'un groupe ────────────────────────────────────
// Lit les feuilles de présence (courses/{courseId}/attendance/{date}/records)
// de tous les cours des inscriptions du groupe, depuis la plus ancienne date
// de début jusqu'à aujourd'hui. Un record est rattaché à l'élève par
// studentId, registrationId ou nom normalisé.
async function loadGroupAttendance(regs) {
  const courseIds = new Set();
  const studentIds = new Set();
  const regIds = new Set();
  const nameKeys = new Set();
  let fromKey = null;

  regs.forEach((reg) => {
    regIds.add(reg.id);
    registrationCourseIds(reg).forEach((id) => courseIds.add(id));
    registrationPersons(reg).forEach((p, idx) => {
      personIdentityKeys(p, reg).forEach((k) => {
        if (k.startsWith('sid:')) studentIds.add(k.slice(4));
        if (k.startsWith('name:')) nameKeys.add(k.slice(5));
      });
      studentIds.add(`${reg.id}_${idx}`); // anciens records indexés par id d'attendee
    });
    const k = dayKey(registrationStartDate(reg));
    if (k && (!fromKey || k < fromKey)) fromKey = k;
  });

  const todayKey = moment().format('YYYY-MM-DD');
  const rows = [];
  const titles = {};

  await Promise.all(Array.from(courseIds).map(async (courseId) => {
    let course;
    try {
      const snap = await getDoc(doc(db, 'dance_courses', courseId));
      if (!snap.exists()) return;
      course = snap.data();
      titles[courseId] = course.title || courseId;
    } catch (e) {
      console.error('Présences : lecture du cours impossible', courseId, e);
      return;
    }
    const keys = Array.from(new Set((course.dates || []).map((d) => {
      const raw = d?.date ?? d;
      const dt = raw?.toDate ? raw.toDate() : raw;
      return moment(dt).format('YYYY-MM-DD');
    }))).filter((k) => k && k !== 'Invalid date' && (!fromKey || k >= fromKey) && k <= todayKey);

    await Promise.all(keys.map(async (k) => {
      try {
        const recSnap = await getDocs(collection(db, `courses/${courseId}/attendance/${k}/records`));
        recSnap.forEach((r) => {
          const d = r.data();
          const nameKey = normalizeText(`${d.firstName || ''} ${d.lastName || ''}`);
          const match = studentIds.has(r.id)
            || (d.studentId && studentIds.has(d.studentId))
            || (d.registrationId && regIds.has(d.registrationId))
            || (nameKey && nameKeys.has(nameKey));
          if (!match) return;
          rows.push({
            key: `${courseId}_${k}_${r.id}`,
            dateKey: k,
            courseId,
            courseTitle: course.title || courseId,
            present: d.present === true ? true : d.present === false ? false : null,
            consumedFromCard: !!d.consumedFromCard,
          });
        });
      } catch (e) {
        console.error('Présences : lecture impossible', courseId, k, e);
      }
    }));
  }));

  return { rows, titles };
}

// ── Composant ────────────────────────────────────────────────────────────

export default function StudentBillingDialog({
  open, group, paymentsByReg = {}, onClose, onChanged, onOpenRegistration,
}) {
  const [payTarget, setPayTarget]       = useState(null);
  const [att, setAtt]                   = useState({ loading: false, rows: [], titles: {}, loadedFor: null });
  const [editRegId, setEditRegId]       = useState(null);
  const [form, setForm]                 = useState({});
  const [busy, setBusy]                 = useState(false);
  const [error, setError]               = useState('');

  const regs = useMemo(() => {
    const list = (group?.registrations || []).map((r) => ({
      ...r,
      _billing: computeBilling(r, paymentsByReg[r.id] || []),
      _kind: registrationKind(r),
    }));
    return list.sort((a, b) => {
      if (KIND_ORDER[a._kind] !== KIND_ORDER[b._kind]) return KIND_ORDER[a._kind] - KIND_ORDER[b._kind];
      return (registrationStartDate(b)?.getTime() || 0) - (registrationStartDate(a)?.getTime() || 0);
    });
  }, [group, paymentsByReg]);

  const billingRegs = regs.filter((r) => r._kind !== 'trial');
  const trials      = regs.filter((r) => r._kind === 'trial');
  const regIdsKey   = regs.map((r) => r.id).sort().join(',');

  // Présences : chargées à l'ouverture (et quand le groupe change de composition)
  useEffect(() => {
    if (!open || !regIdsKey) return undefined;
    if (att.loadedFor === regIdsKey) return undefined;
    let cancelled = false;
    setAtt({ loading: true, rows: [], titles: {}, loadedFor: regIdsKey });
    loadGroupAttendance(regs)
      .then(({ rows, titles }) => { if (!cancelled) setAtt({ loading: false, rows, titles, loadedFor: regIdsKey }); })
      .catch((e) => {
        console.error('Erreur chargement présences:', e);
        if (!cancelled) setAtt({ loading: false, rows: [], titles: {}, loadedFor: regIdsKey });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, regIdsKey]);

  useEffect(() => { if (!open) { setEditRegId(null); setError(''); setAtt({ loading: false, rows: [], titles: {}, loadedFor: null }); } }, [open]);

  // ── Rattachement de chaque présence à une formule ────────────────────────
  const attendanceRows = useMemo(() => {
    const cards   = billingRegs.filter((r) => r._kind === 'card');
    const subs    = billingRegs.filter((r) => r._kind === 'subscription');
    const singles = billingRegs.filter((r) => r._kind === 'single');
    const coversCourse = (r, courseId) => {
      const ids = registrationCourseIds(r);
      return ids.length === 0 || ids.includes(courseId);
    };
    const onDate = (r, k) => r.selectedDate === k || (r.selectedDates || []).includes(k);

    // Séances décomptées sur une carte mais sans feuille de présence
    const rows = [...att.rows];
    cards.forEach((c) => {
      cardCredits(c).consumed.forEach((cl) => {
        const exists = rows.some((row) => consumptionMatches(cl, row.dateKey, row.courseId));
        if (!exists) {
          rows.push({
            key: `card_${c.id}_${cl.dateKey}_${cl.courseId || ''}`,
            dateKey: cl.dateKey,
            courseId: cl.courseId || null,
            courseTitle: cl.courseTitle || att.titles[cl.courseId] || '—',
            present: null,
            fromCardOnly: true,
          });
        }
      });
    });

    return rows
      .map((row) => {
        // Un crédit de carte réellement décompté prime : c'est lui qui a payé la séance
        const card = cards.find((r) => cardCredits(r).consumed.some((cl) => consumptionMatches(cl, row.dateKey, row.courseId)));
        if (card) return { ...row, cover: { type: 'card', reg: card } };
        const sub = subs.find((r) => coversCourse(r, row.courseId)
          && dayKey(registrationStartDate(r)) <= row.dateKey
          && (!r._billing.end || dayKey(r._billing.end) >= row.dateKey));
        if (sub) return { ...row, cover: { type: 'subscription', reg: sub } };
        const trial = trials.find((r) => coversCourse(r, row.courseId) && onDate(r, row.dateKey));
        if (trial) return { ...row, cover: { type: 'trial', reg: trial } };
        const single = singles.find((r) => coversCourse(r, row.courseId) && onDate(r, row.dateKey));
        if (single) return { ...row, cover: { type: 'single', reg: single } };
        const avail = cards
          .filter((r) => coversCourse(r, row.courseId) && cardCredits(r).remaining > 0
            && (dayKey(r.createdAt) || '') <= row.dateKey)
          .sort((a, b) => (readDate(a.createdAt)?.getTime() || 0) - (readDate(b.createdAt)?.getTime() || 0))[0];
        if (avail) return { ...row, cover: { type: 'card_pending', reg: avail } };
        return { ...row, cover: { type: 'none' } };
      })
      .sort((a, b) => b.dateKey.localeCompare(a.dateKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [att.rows, att.titles, regs]);

  const presentCountByReg = useMemo(() => {
    const m = {};
    attendanceRows.forEach((row) => {
      if (!row.cover.reg) return;
      // Présent pointé, ou séance décomptée sur une carte (crédit utilisé même non pointé)
      if (!(row.present === true || (row.present === null && row.cover.type === 'card'))) return;
      if (row.cover.type === 'card_pending' || row.cover.type === 'none') return;
      m[row.cover.reg.id] = (m[row.cover.reg.id] || 0) + 1;
    });
    return m;
  }, [attendanceRows]);

  if (!group) return null;

  const totals = billingRegs.reduce((acc, r) => ({
    remaining: acc.remaining + r._billing.remaining,
    overdue:   acc.overdue + r._billing.overdueAmount,
    paid:      acc.paid + r._billing.paid,
  }), { remaining: 0, overdue: 0, paid: 0 });
  const nextInst = billingRegs
    .flatMap((r) => r._billing.installments.filter((i) => i.status !== 'paid').map((i) => ({ ...i, reg: r })))
    .sort((a, b) => a.dueDate - b.dueDate)[0];
  const uncounted = attendanceRows.filter((r) => r.present === true && r.cover.type === 'card_pending').length;
  const uncovered = attendanceRows.filter((r) => r.present === true && r.cover.type === 'none').length;

  // ── Actions ──────────────────────────────────────────────────────────────

  const run = async (fn, okMessage) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      onChanged?.(okMessage);
    } catch (e) {
      console.error(e);
      setError(e?.message === 'CARD_EXHAUSTED'
        ? 'Cette carte n\'a plus de crédit : il faut une nouvelle carte.'
        : 'L\'opération a échoué (droits Firestore ou connexion).');
    } finally {
      setBusy(false);
    }
  };

  const handleRemovePayment = (reg, entry) => {
    const label = `${MANUAL_METHOD_LABELS[entry.method] || entry.method} de ${money(entry.amount)} du ${formatDay(entry.paidAt)}`;
    if (!window.confirm(`Annuler l'encaissement ${label} ?`)) return;
    run(() => removeManualPayment(reg.id, entry.id), 'Encaissement annulé');
  };

  const openSettings = (reg) => {
    const ov = reg.billingOverrides || {};
    setForm({
      startDate:              ov.startDate || dayKey(registrationStartDate(reg)) || '',
      installmentsTotal:      ov.installmentsTotal ?? (reg._billing.recurring ? reg._billing.total : ''),
      firstInstallmentAmount: ov.firstInstallmentAmount ?? (reg._billing.installments[0]?.amount || ''),
      installmentAmount:      ov.installmentAmount ?? (reg._billing.installments[1]?.amount || ''),
    });
    setEditRegId(editRegId === reg.id ? null : reg.id);
  };

  const handleSaveSettings = (reg) => {
    run(async () => {
      await saveBillingOverrides(reg.id, reg._billing.recurring ? form : {
        startDate: form.startDate, firstInstallmentAmount: form.firstInstallmentAmount,
      });
      setEditRegId(null);
    }, 'Échéancier mis à jour');
  };

  const handleConsume = (row) => {
    run(() => consumeCardCredit(row.cover.reg.id, {
      dateKey: row.dateKey,
      courseId: row.courseId,
      courseTitle: row.courseTitle,
      source: 'admin_billing',
    }), 'Cours décompté de la carte');
  };

  // ── Rendu d'une inscription ──────────────────────────────────────────────

  const renderRegistration = (reg) => {
    const b = reg._billing;
    const meta = billingStatusMeta(b.status);
    const docs = paymentsByReg[reg.id] || [];
    const isCard = reg._kind === 'card';
    const credits = isCard ? cardCredits(reg) : null;
    const editable = !b.isMollie;

    return (
      <Paper key={reg.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
        {/* En-tête */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Typography sx={{ fontWeight: 700 }}>{planLabel(reg)}</Typography>
          <StatusChip meta={meta} />
          {seasonOf(registrationStartDate(reg)) && (
            <Chip size="small" variant="outlined" label={`Saison ${seasonOf(registrationStartDate(reg))}`} />
          )}
          <Box sx={{ flexGrow: 1 }} />
          {editable && (
            <Button size="small" startIcon={<TuneIcon />} onClick={() => openSettings(reg)} sx={{ color: PURPLE }}>
              Échéancier
            </Button>
          )}
          <Button size="small" startIcon={<OpenIcon />} onClick={() => onOpenRegistration?.(reg.id)}>
            Fiche inscription
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {registrationCourseLabel(reg)}
          {b.recurring && <> · {FREQUENCY_LABELS[reg.paymentFrequency] || 'Échelonné'}</>}
          {reg.paymentFrequency === 'annual' && <> · Annuel</>}
          {' · '}{PAYMENT_MODE_LABELS[reg.paymentMode] || (b.isMollie ? 'Mollie' : 'Paiement manuel')}
          {' · '}{b.recurring ? 'début' : 'le'} {formatDay(registrationStartDate(reg))}
          {presentCountByReg[reg.id] ? <> · <strong>{presentCountByReg[reg.id]} présence(s)</strong></> : null}
        </Typography>

        {/* Résumé montants */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 1.5, mb: 1 }}>
          <Typography variant="body2">Total : <strong>{money(b.expected)}</strong></Typography>
          <Typography variant="body2">Reçu : <strong style={{ color: '#2e7d32' }}>{money(b.paid)}</strong></Typography>
          <Typography variant="body2">Reste : <strong>{money(b.remaining)}</strong></Typography>
          {b.overdueAmount > 0 && (
            <Typography variant="body2" sx={{ color: '#c62828' }}>En retard : <strong>{money(b.overdueAmount)}</strong></Typography>
          )}
        </Box>
        {b.expected > 0 && (
          <LinearProgress
            variant="determinate" value={Math.min((b.paid / b.expected) * 100, 100)}
            sx={{ height: 6, borderRadius: 4, mb: 1.5, backgroundColor: '#eee', '& .MuiLinearProgress-bar': { backgroundColor: PURPLE } }}
          />
        )}

        {(b.amountUnknown || b.amountEstimated) && editable && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {b.amountUnknown
              ? 'Aucun montant n\'est enregistré pour cette inscription.'
              : 'Le montant par échéance n\'est pas enregistré : il est repris du montant payé à l\'inscription.'}
            {' '}Vérifie-le via « Échéancier ».
          </Alert>
        )}

        {/* Réglages de l'échéancier (hors Mollie) */}
        <Collapse in={editRegId === reg.id}>
          <Box sx={{ p: 1.5, mb: 1.5, backgroundColor: '#faf7ff', borderRadius: 1 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={b.recurring ? 3 : 6}>
                <TextField
                  label={b.recurring ? 'Début' : 'Date'} type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
                  value={form.startDate || ''} onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                />
              </Grid>
              {b.recurring && (
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Nb d'échéances" size="small" fullWidth type="number"
                    value={form.installmentsTotal ?? ''} onChange={(e) => setForm({ ...form, installmentsTotal: e.target.value })}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={b.recurring ? 3 : 6}>
                <TextField
                  label={b.recurring ? '1re échéance (€)' : 'Montant (€)'} size="small" fullWidth
                  value={form.firstInstallmentAmount ?? ''} onChange={(e) => setForm({ ...form, firstInstallmentAmount: e.target.value })}
                />
              </Grid>
              {b.recurring && (
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Échéances suivantes (€)" size="small" fullWidth
                    value={form.installmentAmount ?? ''} onChange={(e) => setForm({ ...form, installmentAmount: e.target.value })}
                  />
                </Grid>
              )}
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
              <Button size="small" onClick={() => setEditRegId(null)}>Annuler</Button>
              <Button
                size="small" variant="contained" disabled={busy} onClick={() => handleSaveSettings(reg)}
                sx={{ backgroundColor: PURPLE, '&:hover': { backgroundColor: '#6D28D9' } }}
              >
                Enregistrer
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Un champ vidé revient au calcul automatique (septembre → juin, même jour chaque mois).
            </Typography>
          </Box>
        </Collapse>

        {/* Carte 10 : crédits */}
        {isCard && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <CardIcon fontSize="small" sx={{ color: '#e65100' }} />
            <Typography variant="body2">
              {credits.used}/{credits.total} cours utilisés — <strong>{credits.remaining} restant(s)</strong>
            </Typography>
          </Box>
        )}

        {/* Échéancier */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{b.recurring ? 'Échéance' : 'Paiement'}</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Montant</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Reçu</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {b.installments.map((inst) => {
                const st = INSTALLMENT_STATUS[inst.status] || INSTALLMENT_STATUS.upcoming;
                return (
                  <TableRow key={inst.number}>
                    <TableCell>{b.recurring ? `${inst.number}/${b.total}` : 'Unique'}</TableCell>
                    <TableCell sx={{ color: inst.status === 'overdue' || inst.status === 'failed' ? '#c62828' : 'inherit' }}>
                      {formatDay(inst.dueDate)}
                      {inst.overdueDays > 0 && (
                        <Typography variant="caption" sx={{ display: 'block' }}>il y a {inst.overdueDays} j</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{money(inst.amount)}</TableCell>
                    <TableCell><StatusChip meta={st} suffix={inst.partial ? '(partiel)' : ''} /></TableCell>
                    <TableCell>
                      {inst.manualEntries.map((m) => (
                        <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption">
                            {MANUAL_METHOD_LABELS[m.method] || m.method} · {money(m.amount)} · {formatDay(m.paidAt)}
                            {m.note ? ` · ${m.note}` : ''}
                          </Typography>
                          <Tooltip title="Annuler cet encaissement">
                            <IconButton size="small" disabled={busy} onClick={() => handleRemovePayment(reg, m)}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                      {inst.sources.includes('mollie') && (
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          Mollie{inst.paidAt ? ` · ${formatDay(inst.paidAt)}` : ''}
                        </Typography>
                      )}
                      {inst.sources.includes('status') && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Marqué payé
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {inst.status !== 'paid' && inst.status !== 'processing' && (
                        <Button
                          size="small" variant="outlined" startIcon={<PayIcon />}
                          onClick={() => setPayTarget({ reg, installment: inst, groupLabel: group.label })}
                          sx={{ borderColor: PURPLE, color: PURPLE, whiteSpace: 'nowrap' }}
                        >
                          Encaisser
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Historique Mollie */}
        {docs.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">Transactions Mollie</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              {docs
                .slice()
                .sort((a, c) => (readDate(a.createdAt)?.getTime() || 0) - (readDate(c.createdAt)?.getTime() || 0))
                .map((p) => {
                  const st = PAYMENT_DOC_STATUS[p.status] || { label: p.status || '—', bg: '#f5f5f5', color: '#616161' };
                  return (
                    <Tooltip key={p.id} title={`${METHOD_LABELS[p.method] || p.method || ''} · ${st.label}`}>
                      <Chip
                        size="small"
                        label={`${p.installmentNumber ? `#${p.installmentNumber} ` : ''}${formatDay(p.paidAt || p.createdAt)} · ${money(p.amountGross)}`}
                        sx={{ backgroundColor: st.bg, color: st.color }}
                      />
                    </Tooltip>
                  );
                })}
            </Box>
          </Box>
        )}
      </Paper>
    );
  };

  const COVER_LABEL = {
    subscription: (r) => ({ label: planLabel(r.cover.reg), color: '#2e7d32', bg: '#e8f5e9' }),
    card:         () => ({ label: 'Carte 10 (décompté)', color: '#e65100', bg: '#fff3e0' }),
    trial:        () => ({ label: "Cours d'essai", color: '#1565c0', bg: '#e3f2fd' }),
    single:       () => ({ label: 'Cours unique', color: '#6a1b9a', bg: '#f3e5f5' }),
    card_pending: () => ({ label: 'Carte 10 — non décompté', color: '#c62828', bg: '#ffebee' }),
    none:         () => ({ label: 'Aucune formule', color: '#c62828', bg: '#ffebee' }),
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: PURPLE, color: '#fff' }}>
          {group.label}
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
            {[...group.emails, ...group.phones].join(' · ') || 'Pas de contact enregistré'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          {/* Résumé global */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {[
              { label: 'Reste à payer', value: money(totals.remaining), color: 'inherit' },
              { label: 'En retard', value: money(totals.overdue), color: totals.overdue > 0 ? '#c62828' : 'inherit' },
              { label: 'Déjà reçu', value: money(totals.paid), color: '#2e7d32' },
              {
                label: 'Prochain paiement',
                value: nextInst ? `${formatDay(nextInst.dueDate)} · ${money(nextInst.remaining)}` : '—',
                color: 'inherit',
              },
            ].map((k) => (
              <Grid item xs={6} sm={3} key={k.label}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                  <Typography sx={{ fontWeight: 700, color: k.color }}>{k.value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          {billingRegs.length === 0 && (
            <Typography color="text.secondary" sx={{ mb: 2 }}>Aucune formule payante.</Typography>
          )}
          {billingRegs.map(renderRegistration)}

          {trials.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Cours d'essai : {trials.map((t) => `${formatDay(t.selectedDate || t.createdAt)} (${registrationCourseLabel(t)})`).join(' · ')}
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Présences */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Présences</Typography>
            {!att.loading && (
              <>
                <Chip size="small" label={`${attendanceRows.filter((r) => r.present === true).length} présent(s)`} color="success" variant="outlined" />
                <Chip size="small" label={`${attendanceRows.filter((r) => r.present === false).length} absent(s)`} variant="outlined" />
                {uncounted > 0 && <Chip size="small" label={`${uncounted} à décompter`} sx={{ backgroundColor: '#ffebee', color: '#c62828' }} />}
                {uncovered > 0 && <Chip size="small" label={`${uncovered} sans formule`} sx={{ backgroundColor: '#ffebee', color: '#c62828' }} />}
              </>
            )}
          </Box>
          {att.loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={18} /> <Typography variant="body2">Lecture des feuilles de présence…</Typography>
            </Box>
          ) : attendanceRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Aucune présence saisie pour l'instant.</Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Cours</TableCell>
                    <TableCell>Présence</TableCell>
                    <TableCell>Couvert par</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendanceRows.map((row) => {
                    const cover = COVER_LABEL[row.cover.type](row);
                    return (
                      <TableRow key={row.key}>
                        <TableCell>{formatDay(row.dateKey)}</TableCell>
                        <TableCell>{row.courseTitle}</TableCell>
                        <TableCell>
                          {row.present === true && <Chip size="small" label="Présent" color="success" />}
                          {row.present === false && <Chip size="small" label="Absent" color="error" variant="outlined" />}
                          {row.present === null && <Chip size="small" label="Réservé (non pointé)" variant="outlined" />}
                        </TableCell>
                        <TableCell>
                          {row.present === false && row.cover.type !== 'card'
                            ? <Typography variant="caption" color="text.secondary">—</Typography>
                            : <Chip size="small" label={cover.label} sx={{ backgroundColor: cover.bg, color: cover.color }} />}
                        </TableCell>
                        <TableCell align="right">
                          {row.present === true && row.cover.type === 'card_pending' && (
                            <Button size="small" disabled={busy} onClick={() => handleConsume(row)} sx={{ color: PURPLE }}>
                              Décompter
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 2 }}>
            Total reçu toutes formules : {money(round2(totals.paid))}
          </Typography>
          <Button onClick={onClose}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <RecordPaymentDialog
        open={!!payTarget}
        target={payTarget}
        onClose={() => setPayTarget(null)}
        onSaved={(msg) => { setPayTarget(null); onChanged?.(msg); }}
      />
    </>
  );
}
